import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as providerPriceListItemOptionActions from '@redux/modules/providerPriceListItemOption'

import defaultFormState, { requiredFields } from '@models/providerPriceListItemOption'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { ProviderPriceListItemOptionModel, ProviderPriceListItemOptionRequestOptions } from '@models/types'

export const generateUrls = (providerPriceListItemOption?: Partial<ProviderPriceListItemOptionModel>) => {
  const { id } = providerPriceListItemOption || {}

  return {
    editProviderPriceListItemOptionDetailsUrl: `#/systemManager/providerPriceListItemOptions/${id}/edit/details`,
    editProviderPriceListItemOptionUrl: `#/systemManager/providerPriceListItemOptions/${id}/edit/`,
    providerPriceListItemOptionsIndexUrl: '#/systemManager/providerPriceListItemOptions',
  }
}

type CreateProviderPriceListItemOptionParams = {
  dispatch: AppDispatch,
  providerPriceListItemOptionParams: Partial<ProviderPriceListItemOptionModel>,
  requestOptions?: ProviderPriceListItemOptionRequestOptions,
}

const createProviderPriceListItemOption = (params: CreateProviderPriceListItemOptionParams) => {
  const { dispatch, providerPriceListItemOptionParams, requestOptions } = params
  const { createProviderPriceListItemOption: createFn } = providerPriceListItemOptionActions

  return dispatch(createFn(providerPriceListItemOptionParams, requestOptions))
}

type UpdateProviderPriceListItemOptionParams = {
  dispatch: AppDispatch,
  providerPriceListItemOption: ProviderPriceListItemOptionModel,
  providerPriceListItemOptionParams: Partial<ProviderPriceListItemOptionModel>,
  requestOptions?: ProviderPriceListItemOptionRequestOptions,
}

const updateProviderPriceListItemOption = (params: UpdateProviderPriceListItemOptionParams) => {
  const {
    dispatch, providerPriceListItemOption, providerPriceListItemOptionParams, requestOptions,
  } = params
  const { updateProviderPriceListItemOption: updateFn } = providerPriceListItemOptionActions

  const updatedParams = {
    id: providerPriceListItemOption.id,
    ...providerPriceListItemOptionParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteProviderPriceListItemOptionParams = {
  dispatch: AppDispatch,
  providerPriceListItemOption: DeleteParams<ProviderPriceListItemOptionModel>,
}

const deleteProviderPriceListItemOption = (params: DeleteProviderPriceListItemOptionParams) => {
  const { dispatch, providerPriceListItemOption } = params
  const { deleteProviderPriceListItemOption: deleteFn } = providerPriceListItemOptionActions

  return dispatch(deleteFn(providerPriceListItemOption))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useProviderPriceListItemOptionForm(
  providerPriceListItemOption: Partial<ProviderPriceListItemOptionModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const providerPriceListItemOptionForm = useForm(
    defaultFormState,
    { entity: providerPriceListItemOption, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [providerPriceListItemOption.id, providerPriceListItemOption.cache_key],
  )

  return {
    ...providerPriceListItemOptionForm,
  }
}

export const useRelations = (providerPriceListItemOption: Partial<ProviderPriceListItemOptionModel> = {}) => {
  // @ts-expect-error TS2339
  const { related_entity_id } = providerPriceListItemOption

  // @ts-expect-error TS2339
  const { relatedEntities } = useSelector(reduxState => reduxState.entities)

  const relatedEntity = related_entity_id && relatedEntities[related_entity_id] ? relatedEntities[related_entity_id] : {}

  return {
    relatedEntity,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: ProviderPriceListItemOptionRequestOptions,
}

function useProviderPriceListItemOption(initEntity: Partial<ProviderPriceListItemOptionModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: providerPriceListItemOption }: { entity: ProviderPriceListItemOptionModel} = useLatestEntity(initEntity, 'providerPriceListItemOptions')
  const { id } = providerPriceListItemOption

  const dispatch = useDispatch()

  const { relatedEntity } = useRelations(providerPriceListItemOption)

  useReduxAction(
    'providerPriceListItemOptions',
    'loadProviderPriceListItemOption',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.providerPriceListItemOptions)

  return {
    callbacks: {
      createProviderPriceListItemOption: (
        providerPriceListItemOptionParams: Partial<ProviderPriceListItemOptionModel>,
        entityOptions?: ProviderPriceListItemOptionRequestOptions,
      ) => (
        createProviderPriceListItemOption({
          dispatch,
          providerPriceListItemOptionParams,
          requestOptions: entityOptions,
        })
      ),
      deleteProviderPriceListItemOption: () => (
        deleteProviderPriceListItemOption({ dispatch, providerPriceListItemOption })
      ),
      toggleVisibility: (entityOptions?: ProviderPriceListItemOptionRequestOptions) => (
        updateProviderPriceListItemOption({
          dispatch,
          providerPriceListItemOption,
          providerPriceListItemOptionParams: { hidden: !providerPriceListItemOption.hidden },
          requestOptions: entityOptions,
        })
      ),
      updateProviderPriceListItemOption: (
        providerPriceListItemOptionParams: Partial<ProviderPriceListItemOptionModel>,
        entityOptions?: ProviderPriceListItemOptionRequestOptions,
      ) => (
        updateProviderPriceListItemOption({
          providerPriceListItemOption, providerPriceListItemOptionParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    loading: loading && !loadedIds.includes(providerPriceListItemOption.id),
    providerPriceListItemOption,
    relatedEntity,
    updating,
    urls: generateUrls(providerPriceListItemOption),
  }
}

export default useProviderPriceListItemOption
